import { useQuery } from '@tanstack/react-query';
import { api, Clip } from '../api/client';

interface SampleClipPickerProps {
  onSelect: (clipId: string | null) => void;
  selectedClipId: string | null;
}

export default function SampleClipPicker({ onSelect, selectedClipId }: SampleClipPickerProps) {
  const { data, isLoading, error } = useQuery({
    queryKey: ['samples'],
    queryFn: () => api.getSamples(12),
  });

  if (isLoading) {
    return (
      <div className="text-center py-8">
        <div className="w-8 h-8 border-4 border-arena-accent border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
        <p className="text-arena-muted">Loading sample clips...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="text-center py-8">
        <p className="text-red-400">Failed to load sample clips</p>
      </div>
    );
  }

  const samples = data?.samples || [];

  if (samples.length === 0) {
    return (
      <div className="text-center py-8">
        <p className="text-arena-muted">No sample clips available yet</p>
      </div>
    );
  }

  return (
    <div>
      {/* Random Option */}
      <button
        onClick={() => onSelect(null)}
        className={`w-full mb-6 p-4 rounded-xl border-2 transition-all duration-200 flex items-center justify-center gap-3 ${
          selectedClipId === null
            ? 'border-arena-accent bg-arena-accent/10 text-arena-accent'
            : 'border-arena-border bg-arena-surface hover:border-arena-accent/50'
        }`}
      >
        <span className="text-2xl">🎲</span>
        <div className="text-left">
          <div className="font-semibold">Surprise Me!</div>
          <div className="text-sm text-arena-muted">Random clip from our collection</div>
        </div>
      </button>

      {/* Clip Grid */}
      <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-4">
        {samples.map((clip: Clip) => (
          <ClipCard
            key={clip.id}
            clip={clip}
            isSelected={selectedClipId === clip.id}
            onSelect={() => onSelect(clip.id)}
          />
        ))}
      </div>
    </div>
  );
}

interface ClipCardProps {
  clip: Clip;
  isSelected: boolean;
  onSelect: () => void;
}

function ClipCard({ clip, isSelected, onSelect }: ClipCardProps) {
  const formatDuration = (seconds: number) => {
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  return (
    <button
      onClick={onSelect}
      className={`group relative rounded-xl overflow-hidden border-2 transition-all duration-200 ${
        isSelected
          ? 'border-arena-accent ring-2 ring-arena-accent/30'
          : 'border-arena-border hover:border-arena-accent/50'
      }`}
    >
      {/* Thumbnail */}
      <div className="aspect-video bg-arena-bg relative">
        {clip.thumbnail_url ? (
          <img
            src={clip.thumbnail_url}
            alt={clip.title}
            className="w-full h-full object-cover"
            loading="lazy"
          />
        ) : (
          <div className="w-full h-full flex items-center justify-center text-4xl">
            🎬
          </div>
        )}

        {/* Duration badge */}
        <div className="absolute bottom-2 right-2 bg-black/80 text-white text-xs px-2 py-1 rounded">
          {formatDuration(clip.duration_seconds)}
        </div>

        {/* Selection indicator */}
        {isSelected && (
          <div className="absolute inset-0 bg-arena-accent/20 flex items-center justify-center">
            <div className="w-10 h-10 bg-arena-accent rounded-full flex items-center justify-center">
              <svg className="w-6 h-6 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
              </svg>
            </div>
          </div>
        )}

        {/* Hover overlay */}
        {!isSelected && (
          <div className="absolute inset-0 bg-black/0 group-hover:bg-black/30 transition-all duration-200 flex items-center justify-center opacity-0 group-hover:opacity-100">
            <span className="text-white text-sm font-medium">Select</span>
          </div>
        )}
      </div>

      {/* Title */}
      <div className="p-2 bg-arena-surface">
        <p className="text-sm font-medium truncate" title={clip.title}>
          {clip.title}
        </p>
      </div>
    </button>
  );
}
